/**
 * T.Z49 — the DOM half of the UseDesk widget, with the browser injected.
 *
 * Every function takes the `window`/`document` it touches, the way
 * `../auth/session-provider` takes its `Storage`: the plain-Node `unit` project
 * calls these with structural fakes and never reaches a network or a real DOM
 * (the repo has no jsdom, on purpose). The React mount is `./UsedeskWidget.tsx`.
 *
 * The widget API is the one UseDesk documents (`api.usedocs.com/article/51387`)
 * and the ISM2-6304 precedent in the sibling platform repo uses:
 *
 *  - the script tag is the standard loader for the tenant widget — the widget id
 *    is in the URL path, so no settings object is needed and `usedeskMessenger`
 *    "initializes automatically" once the script runs;
 *  - `window.__widgetInitCallback` fires when the widget appears on the page —
 *    set BEFORE the script tag so the very first paint is already identified;
 *  - `usedeskMessenger.userIdentify({name, email})` carries the visitor's
 *    identity into the tickets they open;
 *  - `usedeskMessenger.toggle(false)` hides the launcher bubble;
 *  - T.Z79 — `generic.setOptions({inversed: true})` moves the launcher to the
 *    bottom-left corner ({@link placeUsedeskLauncherLeft}; `inversed` is the
 *    widget's own «слева» configuration, there is no documented `position` call).
 *
 * Two UseDesk behaviours we deliberately do NOT fight (both quoted in the docs):
 * identity is attached only to a chat the client STARTS in the same browser
 * session, and `userIdentify` never rewrites a client UseDesk already knows.
 * That is why "clear identity on logout" here means hide the widget + drop our
 * hook — the full-page navigation the logout goes through is what actually ends
 * the attribution, and a fresh login identifies the new account in its own
 * session.
 */

import {
  USEDESK_SCRIPT_ID,
  USEDESK_SCRIPT_URL,
  identityChanged,
  type UsedeskIdentity,
} from './usedesk-model';

/** The slice of `window.usedeskMessenger` this feature needs. */
export interface UsedeskMessenger {
  userIdentify?(data: { name?: string; email?: string }): unknown;
  toggle?(visible: boolean): unknown;
  /**
   * T.Z79 — the widget's own options store, as its init hands it to
   * `__widgetInitCallback` (the instance carries the live internal API under
   * `generic`; `setOptions` MERGES its argument into the widget's configuration —
   * verified against the widget bundle for this tenant's widget version).
   */
  generic?: {
    setOptions?(options: Record<string, unknown>): unknown;
  };
}

/** The globals the widget script and we share. */
export interface UsedeskWindow {
  usedeskMessenger?: UsedeskMessenger;
  __widgetInitCallback?: (widget: unknown) => void;
}

/**
 * The one method the injected `<script>` needs (`setAttribute` covers `id`,
 * `src` and `async`, so both a real `HTMLScriptElement` and a test fake fit).
 */
export interface UsedeskScriptElement {
  setAttribute(name: string, value: string): void;
}

/** The slice of `Document` the loader touches. */
export interface UsedeskDocument {
  createElement(tag: string): UsedeskScriptElement;
  getElementById(id: string): unknown;
  head: { appendChild(node: unknown): void };
}

/** `injected` = we appended a tag; `already-present` = the tag is there, we only refreshed the hook. */
export type UsedeskLoadResult = 'injected' | 'already-present' | 'no-head';

/**
 * Make the widget available on the page, identified as `identity` if it is
 * known. Idempotent: the hook is (re)installed before anything else, and the
 * script tag is appended only when it is not there yet.
 */
export function loadUsedeskWidget(
  win: UsedeskWindow,
  doc: UsedeskDocument,
  identity: UsedeskIdentity | null,
): UsedeskLoadResult {
  win.__widgetInitCallback = (widget: unknown) => {
    // T.Z79 — the corner is set at widget-appear time, together with the identity:
    // this is the only moment a FIRST load can be shaped (the launcher already
    // painted by the time the hook runs, so the curator sees at most one frame in
    // the old corner).
    placeUsedeskLauncherLeft(win, widget instanceof Object ? (widget as UsedeskMessenger) : undefined);
    if (identity) identifyUsedesk(win, identity);
  };
  if (doc.getElementById(USEDESK_SCRIPT_ID) !== null) return 'already-present';
  if (!doc.head) return 'no-head';
  const script = doc.createElement('script');
  script.setAttribute('id', USEDESK_SCRIPT_ID);
  script.setAttribute('src', USEDESK_SCRIPT_URL);
  script.setAttribute('async', '');
  doc.head.appendChild(script);
  return 'injected';
}

/**
 * T.Z79 — the launcher goes to the BOTTOM-LEFT corner. UseDesk publishes no
 * `position` in its JS API: the corner is a widget configuration option,
 * `inversed` (the admin panel's «виджет слева», shipped embedded in the tenant's
 * script as `inversed: false` — this is what flips the launcher container from
 * `right: 20px` to `left: 20px`, the chat frame from `right: 95px` to
 * `left: 95px`, and turns the tooltip to the matching side). `setOptions` merges
 * its argument into that configuration reactively, so the ONE option below is the
 * whole move — no CSS of ours over the widget's DOM, nothing else changes.
 *
 * The call is best-effort by the house rule for this widget: a messenger that has
 * not finished mounting (or a future bundle that renamed the internal handle)
 * answers `false` and the launcher stays in the corner the UseDesk panel sets —
 * the chat still works, nothing here may take the page down.
 */
export const USEDESK_LAUNCHER_LEFT_OPTIONS: Record<string, unknown> = { inversed: true };

/** `'left'` = applied, `'absent'` = the widget has no options API yet, `'error'` = it threw. */
export type UsedeskPlacementResult = 'left' | 'absent' | 'error';

export function placeUsedeskLauncherLeft(win: UsedeskWindow, widget?: UsedeskMessenger): UsedeskPlacementResult {
  const messenger = widget ?? win.usedeskMessenger;
  const optionsApi = messenger?.generic;
  const setOptions = optionsApi?.setOptions;
  if (typeof setOptions !== 'function') return 'absent';
  try {
    setOptions.call(optionsApi, USEDESK_LAUNCHER_LEFT_OPTIONS);
    return 'left';
  } catch {
    return 'error';
  }
}

/**
 * Identify the visitor. False when the widget is not there yet (the script is
 * still downloading — `__widgetInitCallback` covers that case), has no
 * `userIdentify`, or the call throws: a chat that opens unidentified beats a
 * chat that does not open, so nothing here propagates.
 */
export function identifyUsedesk(win: UsedeskWindow, identity: UsedeskIdentity): boolean {
  const messenger = win.usedeskMessenger;
  if (!messenger || typeof messenger.userIdentify !== 'function') return false;
  try {
    messenger.userIdentify({ name: identity.name, email: identity.email });
    return true;
  } catch {
    return false;
  }
}

/**
 * Hide the launcher and drop our hook — the logout / anonymous side of the
 * policy. UseDesk publishes no "forget this client" call (`userIdentify`
 * explicitly never rewrites a known client), so this is best-effort and the
 * page navigation that follows a logout does the real work.
 */
export function hideUsedeskWidget(win: UsedeskWindow): void {
  win.__widgetInitCallback = undefined;
  const messenger = win.usedeskMessenger;
  if (!messenger || typeof messenger.toggle !== 'function') return;
  try {
    messenger.toggle(false);
  } catch {
    // best-effort — a hidden widget that fails to hide must not break the app
  }
}

/** What one render of the mount knows. `identity: null` IS the anonymous boot. */
export interface UsedeskSyncInput {
  /** The mount policy's answer for this run (`usedeskShouldMount`). */
  mount: boolean;
  /** The session identity as of NOW; `null` = anonymous (no `userIdentify`). */
  identity: UsedeskIdentity | null;
  /** The identity the widget last IDENTIFIED with (`null` never identified). */
  carried: UsedeskIdentity | null;
}

/** What the caller must keep for the next run (`carried`) and what was done. */
export interface UsedeskSyncResult {
  carried: UsedeskIdentity | null;
  /** `'mounted'` = booted / re-identified this run; `'hidden'` = kept off the page. */
  action: 'mounted' | 'hidden';
}

/**
 * T.Z116 — the ONE per-run decision, framework-free, so the boot-condition
 * matrix is unit-pinnable without a DOM: the component calls this on every
 * change of flag / session status / identity and keeps `result.carried`.
 *
 *  - `mount: false` (the flag is off, or the session question is still open) →
 *    the widget has no business on the page: hide + drop the hook, carried dies.
 *  - anonymous run (`identity: null`): boot WITHOUT an identify — that is the
 *    /login and /logged-out widget — EXCEPT when the widget already carries a
 *    session identity and the session then died under us (the client-side
 *    `ProtectedRoute` bounce, the one logout path that is NOT a page load).
 *    There the launcher is hidden: UseDesk still holds the PREVIOUS user's
 *    attribution (it never forgets a client), and a fresh chat would be filed
 *    under somebody else's name. A fresh page load on /login or /logged-out
 *    starts with `carried: null`, so the anonymous widget boots visible there —
 *    and every real sign-in is a page load, which is what revives it.
 *  - identified run: boot (idempotently) and identify when the identity moved —
 *    the T.Z49 first login, account switch and re-login-without-reload paths,
 *    unchanged.
 */
export function syncUsedeskWidget(win: UsedeskWindow, doc: UsedeskDocument, input: UsedeskSyncInput): UsedeskSyncResult {
  if (!input.mount) {
    hideUsedeskWidget(win);
    return { carried: null, action: 'hidden' };
  }
  if (input.identity === null) {
    if (input.carried !== null) {
      hideUsedeskWidget(win);
      return { carried: null, action: 'hidden' };
    }
    loadUsedeskWidget(win, doc, null);
    placeUsedeskLauncherLeft(win);
    return { carried: null, action: 'mounted' };
  }
  loadUsedeskWidget(win, doc, input.identity);
  // T.Z79 — the direct call covers every run AFTER the widget is already on the
  // page (a re-mount, a re-login without a page reload); the hook installed by
  // `loadUsedeskWidget` covers the first paint while the script is downloading.
  placeUsedeskLauncherLeft(win);
  let carried = input.carried;
  if (identityChanged(carried, input.identity) && identifyUsedesk(win, input.identity)) {
    // Only a DELIVERED identify is remembered, so a not-yet-ready messenger is
    // retried on the next run (the hook answers that window in the meantime).
    carried = input.identity;
  }
  return { carried, action: 'mounted' };
}
